import type { TemplateQuery } from '@/services/api/dataApi';
import type { CompositeJoinKey, CompositeQueryDefinition } from '@/types/chartDataSource';
import {
  getDisplayResultTypeLabel,
  type CreateResultMetadata,
  type CreateResultTypeLabel,
  type CreateTemplateResult,
} from './DataDiscoveryModal.createLogic';

const FORBIDDEN_DYNAMIC_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const PREVIEW_ROW_LIMIT = 5;
const PREVIEW_COLUMN_LIMIT = 6;
const LOW_SOURCE_CONFIDENCE_THRESHOLD = 0.55;

export type CreateFeedbackFactId =
  | 'detected-type'
  | 'source-count'
  | 'match-strategy'
  | 'series-count'
  | 'low-confidence-source-match';

export interface CreateFeedbackFact {
  id: CreateFeedbackFactId;
  label: string;
  tone?: 'warning';
}

export interface CreatePreviewModel {
  displayResultTypeLabel: CreateResultTypeLabel;
  feedbackFacts: CreateFeedbackFact[];
  sourceSummary: string | null;
  schemaSummary: string | null;
  sourceLabels: string[];
  schemaLabels: string[];
  fieldLabels: string[];
  seriesLabels: string[];
  timeRangeLabel: string | null;
  limitLabel: string | null;
  joinSummary: string | null;
  matchingSummary: string | null;
  previewRows: Record<string, unknown>[];
  previewColumns: string[];
  previewColumnLabels: Record<string, string>;
  reviewNote: string | null;
  confidenceNote: string | null;
  needsReview: boolean;
  needsClarification: boolean;
  reviewAcceptanceAllowed: boolean;
  saveDisabledReason: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isSafeDynamicKey(key: string): boolean {
  return !FORBIDDEN_DYNAMIC_KEYS.has(key);
}

export function normalizeCreatePreviewRow(row: unknown): Record<string, unknown> | null {
  if (!isRecord(row)) {
    return null;
  }

  const normalized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row)) {
    const nestedRecord = isRecord(value) ? value : null;
    if (key === '_id' && nestedRecord) {
      for (const [nestedKey, nestedValue] of Object.entries(nestedRecord)) {
        if (isSafeDynamicKey(nestedKey)) {
          normalized[nestedKey] = nestedValue;
        }
      }
      continue;
    }

    if (isSafeDynamicKey(key)) {
      normalized[key] = value;
    }
  }

  return normalized;
}

export function formatCreatePreviewValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(', ');
  if (isRecord(value)) {
    const statusValue = getStatusPreviewLabel(value);
    if (statusValue) {
      return statusValue;
    }

    return stringifyRecord(value);
  }
  return String(value);
}

export function buildCreateTemplatePreviewModel(
  result: Extract<CreateTemplateResult, { kind: 'template' }>,
): CreatePreviewModel {
  const templateQuery = result.response.templateQuery;
  const schemaLabel = result.response.source?.name ?? templateQuery?.schemaId ?? null;
  const previewRows = normalizePreviewRows(result.response.data ?? []);
  const fieldLabels = templateQuery?.measures.map(formatTemplateMeasureLabel) ?? [];
  const seriesLabels = templateQuery?.measures.map(formatTemplateSeriesLabel) ?? [];
  const sourceCount = schemaLabel || templateQuery ? 1 : 0;

  return buildBasePreviewModel(result.metadata, {
    feedbackFacts: buildTemplateFeedbackFacts(result.metadata, sourceCount, seriesLabels.length, result.response),
    sourceSummary: schemaLabel,
    schemaSummary: schemaLabel,
    sourceLabels: schemaLabel ? [schemaLabel] : [],
    schemaLabels: schemaLabel ? [schemaLabel] : [],
    fieldLabels,
    seriesLabels,
    timeRangeLabel: formatTemplateTimeRangeLabel(
      templateQuery,
      result.response.templateVariables?.timeRange?.default,
    ),
    limitLabel: formatLimitLabel(templateQuery?.limit),
    joinSummary: null,
    matchingSummary: null,
    previewRows,
    previewColumnLabels: buildTemplatePreviewColumnLabels(
      previewRows,
      templateQuery,
    ),
  });
}

export function buildCreateCompositePreviewModel(
  result: Extract<CreateTemplateResult, { kind: 'composite' }>,
): CreatePreviewModel {
  const definition = result.definition;
  const sourceLabels = definition.sources.map((source) => source.label ?? source.id).filter(isNonEmptyString);
  const schemaLabels = uniqueStrings(
    definition.sources
      .map((source) => source.query.schemaName ?? source.query.schemaId)
      .filter(isNonEmptyString),
  );
  const projectedFields = definition.fields
    .map((field) => formatReadablePreviewLabel(field.alias))
    .filter(isNonEmptyString);
  const computedFields = (definition.computedFields ?? [])
    .map((field) => formatReadablePreviewLabel(field.name))
    .filter(isNonEmptyString);
  const fieldLabels = [...projectedFields, ...computedFields];
  const seriesCount = countCompositeChartSeries(definition);
  const previewRows = normalizePreviewRows(result.previewRows);

  return buildBasePreviewModel(result.metadata, {
    feedbackFacts: buildCompositeFeedbackFacts(result.metadata, definition, seriesCount),
    sourceSummary: formatCountLabel(definition.sources.length, 'source'),
    schemaSummary: schemaLabels.length > 0 ? schemaLabels.join(', ') : null,
    sourceLabels,
    schemaLabels,
    fieldLabels,
    seriesLabels: fieldLabels,
    timeRangeLabel: formatCompositeTimeRangeLabel(definition),
    limitLabel: formatLimitLabel(definition.limit),
    joinSummary: formatJoinSummary(definition),
    matchingSummary: formatMatchingSummary(definition),
    previewRows,
    previewColumnLabels: buildCompositePreviewColumnLabels(
      previewRows,
      definition,
    ),
  });
}

type BasePreviewModelInput = Omit<
  CreatePreviewModel,
  | 'displayResultTypeLabel'
  | 'previewColumns'
  | 'previewColumnLabels'
  | 'reviewNote'
  | 'confidenceNote'
  | 'needsReview'
  | 'needsClarification'
  | 'reviewAcceptanceAllowed'
  | 'saveDisabledReason'
> & {
  previewColumnLabels?: Record<string, string>;
};

function buildBasePreviewModel(
  metadata: CreateResultMetadata,
  values: BasePreviewModelInput,
): CreatePreviewModel {
  const reviewNote = getReviewNote(metadata);
  const previewColumns = selectPreviewColumns(values.previewRows);

  return {
    displayResultTypeLabel: getDisplayResultTypeLabel(metadata),
    ...values,
    previewColumns,
    previewColumnLabels: buildPreviewColumnLabels(
      previewColumns,
      values.previewColumnLabels,
    ),
    reviewNote,
    confidenceNote: reviewNote,
    needsReview: metadata.needsReview,
    needsClarification: metadata.needsClarification,
    reviewAcceptanceAllowed: metadata.reviewAcceptanceAllowed !== false,
    saveDisabledReason: metadata.saveDisabledReason ?? null,
  };
}

function selectPreviewColumns(previewRows: Record<string, unknown>[]): string[] {
  const firstRow = previewRows[0];
  if (!firstRow) {
    return [];
  }

  return Object.keys(firstRow)
    .filter((column) => !isEmptyIdentifierPreviewColumn(column, previewRows))
    .slice(0, PREVIEW_COLUMN_LIMIT);
}

function isEmptyIdentifierPreviewColumn(column: string, previewRows: Record<string, unknown>[]): boolean {
  if (!isIdentifierPreviewColumn(column)) {
    return false;
  }

  return previewRows.every((row) => isBlankPreviewValue(row[column]));
}

function isIdentifierPreviewColumn(column: string): boolean {
  return column === '_id' || column === 'id' || formatReadablePreviewLabel(column) === 'Id';
}

function isBlankPreviewValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return true;
  }

  return formatCreatePreviewValue(value) === '—';
}

function buildTemplateFeedbackFacts(
  metadata: CreateResultMetadata,
  sourceCount: number,
  seriesCount: number,
  response: Extract<CreateTemplateResult, { kind: 'template' }>['response'],
): CreateFeedbackFact[] {
  return [
    createDetectedFact(metadata),
    createSourceCountFact(sourceCount),
    createSeriesCountFact(seriesCount),
    createLowConfidenceFact(response),
  ].filter(isFeedbackFact);
}

function buildCompositeFeedbackFacts(
  metadata: CreateResultMetadata,
  definition: CompositeQueryDefinition,
  seriesCount: number,
): CreateFeedbackFact[] {
  return [
    createDetectedFact(metadata),
    createSourceCountFact(definition.sources.length),
    createMatchStrategyFact(definition),
    createSeriesCountFact(seriesCount),
  ].filter(isFeedbackFact);
}

function createDetectedFact(metadata: CreateResultMetadata): CreateFeedbackFact {
  return {
    id: 'detected-type',
    label: `Detected: ${getFeedbackResultTypeLabel(metadata)}`,
  };
}

function getFeedbackResultTypeLabel(metadata: CreateResultMetadata): CreateResultTypeLabel {
  if (metadata.resultTypeLabel === 'Needs Clarification' || metadata.resultTypeLabel === 'Needs Review') {
    return metadata.resultTypeLabel;
  }

  return getDisplayResultTypeLabel(metadata);
}

function createSourceCountFact(sourceCount: number): CreateFeedbackFact | null {
  if (sourceCount <= 0) {
    return null;
  }

  return {
    id: 'source-count',
    label: `Using ${sourceCount} data ${sourceCount === 1 ? 'source' : 'sources'}`,
  };
}

function createSeriesCountFact(seriesCount: number): CreateFeedbackFact | null {
  if (seriesCount <= 0) {
    return null;
  }

  return {
    id: 'series-count',
    label: `Created ${seriesCount} chart series`,
  };
}

function createLowConfidenceFact(
  response: Extract<CreateTemplateResult, { kind: 'template' }>['response'],
): CreateFeedbackFact | null {
  const confidence = response.debug?.matchedSources[0]?.confidence;
  if (typeof confidence !== 'number' || confidence >= LOW_SOURCE_CONFIDENCE_THRESHOLD) {
    return null;
  }

  return {
    id: 'low-confidence-source-match',
    label: 'Low confidence: source match may need review',
    tone: 'warning',
  };
}

function createMatchStrategyFact(definition: CompositeQueryDefinition): CreateFeedbackFact | null {
  const matchLabel = getMatchStrategyLabel(definition.joinKeys);
  if (!matchLabel) {
    return null;
  }

  return {
    id: 'match-strategy',
    label: matchLabel,
  };
}

function getMatchStrategyLabel(joinKeys: CompositeJoinKey[]): string | null {
  const nearestTimestampKey = joinKeys.find((joinKey) => joinKey.kind === 'nearest-timestamp');
  if (nearestTimestampKey) {
    return 'Matched by nearest timestamp';
  }

  const timeBucketKey = joinKeys.find((joinKey) => joinKey.kind === 'time-bucket');
  if (timeBucketKey?.granularity) {
    return `Matched by ${timeBucketKey.granularity}`;
  }
  if (timeBucketKey) {
    return 'Matched by timestamp';
  }

  const timestampKey = joinKeys.find(joinKeyUsesTimestamp);
  return timestampKey ? 'Matched by timestamp' : null;
}

function joinKeyUsesTimestamp(joinKey: CompositeJoinKey): boolean {
  return isTimestampLikeField(joinKey.left) || isTimestampLikeField(joinKey.right);
}

function countCompositeChartSeries(definition: CompositeQueryDefinition): number {
  const projectedSeriesCount = definition.fields.filter(
    (field) => !isJoinFieldAlias(field.alias, definition.joinKeys),
  ).length;
  return projectedSeriesCount + (definition.computedFields?.length ?? 0);
}

function isJoinFieldAlias(alias: string, joinKeys: CompositeJoinKey[]): boolean {
  return (
    joinKeys.some((joinKey) => fieldsMatch(alias, joinKey.left) || fieldsMatch(alias, joinKey.right)) ||
    isTimestampLikeField(alias)
  );
}

function fieldsMatch(left: string, right: string): boolean {
  return normalizeFieldName(left) === normalizeFieldName(right);
}

function stripImplementationFieldPrefix(value: string): string {
  return value
    .replace(/^(?:data|metadata)[._-]+/i, '')
    .replace(/^(?:data|metadata)(?=[A-Z])/, '');
}

function normalizeFieldName(value: string): string {
  return stripImplementationFieldPrefix(value)
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

function isTimestampLikeField(value: string): boolean {
  return /\b(date|datetime|timestamp|time)\b/i.test(value.replace(/[_-]+/g, ' '));
}

function isFeedbackFact(fact: CreateFeedbackFact | null): fact is CreateFeedbackFact {
  return fact !== null;
}

function normalizePreviewRows(rows: unknown[]): Record<string, unknown>[] {
  const normalizedRows = rows.flatMap((row) => {
    const normalizedRow = normalizeCreatePreviewRow(row);
    return normalizedRow ? [normalizedRow] : [];
  });

  return sortPreviewRowsNewestFirst(normalizedRows).slice(0, PREVIEW_ROW_LIMIT);
}

function sortPreviewRowsNewestFirst(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  const timeColumn = findPreviewTimeColumn(rows);
  if (!timeColumn) {
    return rows;
  }

  return [...rows].sort((left, right) => {
    const leftValue = getPreviewTimeSortValue(left[timeColumn]);
    const rightValue = getPreviewTimeSortValue(right[timeColumn]);
    if (leftValue === null && rightValue === null) {
      return 0;
    }
    if (leftValue === null) {
      return 1;
    }
    if (rightValue === null) {
      return -1;
    }

    return rightValue - leftValue;
  });
}

function findPreviewTimeColumn(rows: Record<string, unknown>[]): string | null {
  const columns = uniqueStrings(rows.flatMap((row) => Object.keys(row)));
  return columns.find((column) =>
    isTimestampLikeField(column) &&
    rows.filter((row) => getPreviewTimeSortValue(row[column]) !== null).length >= 2,
  ) ?? null;
}

function getPreviewTimeSortValue(value: unknown): number | null {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedDate = Date.parse(trimmedValue);
    if (Number.isFinite(parsedDate)) {
      return parsedDate;
    }

    const parsedNumber = Number(trimmedValue);
    return Number.isFinite(parsedNumber) ? parsedNumber : null;
  }

  return null;
}

function getStatusPreviewLabel(value: Record<string, unknown>): string | null {
  for (const key of ['state', 'status', 'mode', 'value']) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  const openLevel = value.open_level;
  const closeLevel = value.close_level;
  if (typeof openLevel === 'number' || typeof closeLevel === 'number') {
    return 'closed';
  }

  return null;
}

function stringifyRecord(value: Record<string, unknown>): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function formatTemplateMeasureLabel(measure: TemplateQuery['measures'][number]): string {
  const fieldLabel = formatReadablePreviewLabel(measure.field);
  const aliasLabel = formatReadablePreviewLabel(measure.alias);
  if (isGenericCountMeasure(measure)) {
    return aliasLabel ?? 'Count';
  }

  if (aliasLabel && isGenericValueField(measure.field)) {
    return aliasLabel;
  }

  const baseLabel = `${measure.aggregation}(${fieldLabel ?? measure.field})`;
  return aliasLabel ? `${baseLabel} -> ${aliasLabel}` : baseLabel;
}

function isGenericValueField(field: string): boolean {
  return normalizeFieldName(field) === 'value';
}

function isGenericCountMeasure(measure: TemplateQuery['measures'][number]): boolean {
  return (
    measure.aggregation.trim().toLowerCase() === 'count' &&
    measure.field.trim() === '*'
  );
}

function formatTemplateSeriesLabel(measure: TemplateQuery['measures'][number]): string {
  if (isGenericCountMeasure(measure)) {
    return formatReadablePreviewLabel(measure.alias) ?? 'Count';
  }

  return (
    formatReadablePreviewLabel(measure.alias) ??
    formatReadablePreviewLabel(measure.field) ??
    measure.field
  );
}

function formatReadablePreviewLabel(value: string | null | undefined): string | null {
  const fieldValue = value ? stripImplementationFieldPrefix(value) : null;
  const normalized = fieldValue
    ?.replace(/^metadata[._-](timestamp|datetime|date|time)$/i, '$1')
    .replace(/\./g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized || normalized === '*') {
    return null;
  }

  return normalized
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function buildPreviewColumnLabels(
  previewColumns: string[],
  overrides: Record<string, string> | undefined,
): Record<string, string> {
  const labels: Record<string, string> = {};

  for (const column of previewColumns) {
    labels[column] =
      overrides?.[column] ?? formatReadablePreviewLabel(column) ?? column;
  }

  return labels;
}

interface PreviewColumnLabelCandidate {
  field: string;
  label: string;
}

function buildPreviewColumnLabelMap(
  previewRows: Record<string, unknown>[],
  candidates: PreviewColumnLabelCandidate[],
): Record<string, string> {
  const firstRow = previewRows[0];
  if (!firstRow) {
    return {};
  }

  const labels: Record<string, string> = {};

  for (const column of Object.keys(firstRow).slice(0, PREVIEW_COLUMN_LIMIT)) {
    const matchingCandidate = candidates.find((candidate) =>
      fieldsMatch(column, candidate.field),
    );
    const label = matchingCandidate?.label ?? formatReadablePreviewLabel(column);
    if (label) {
      labels[column] = label;
    }
  }

  return labels;
}

function buildTemplatePreviewColumnLabels(
  previewRows: Record<string, unknown>[],
  query: TemplateQuery | undefined,
): Record<string, string> {
  const candidates: PreviewColumnLabelCandidate[] = [];

  for (const group of query?.groupBy ?? []) {
    if (typeof group === 'string') {
      const label = formatReadablePreviewLabel(group);
      if (label) {
        candidates.push({ field: group, label });
      }
      continue;
    }

    const label =
      formatReadablePreviewLabel(group.granularity) ??
      formatReadablePreviewLabel(group.field);
    if (label) {
      candidates.push({ field: group.field, label });
    }
  }

  for (const measure of query?.measures ?? []) {
    const aliasLabel = formatReadablePreviewLabel(measure.alias);
    if (measure.alias && aliasLabel) {
      candidates.push({ field: measure.alias, label: aliasLabel });
    }

    const fieldLabel = formatReadablePreviewLabel(measure.field);
    if (fieldLabel) {
      candidates.push({ field: measure.field, label: fieldLabel });
    }
  }

  return buildPreviewColumnLabelMap(previewRows, candidates);
}

function buildCompositePreviewColumnLabels(
  previewRows: Record<string, unknown>[],
  definition: CompositeQueryDefinition,
): Record<string, string> {
  const candidates: PreviewColumnLabelCandidate[] = [];

  for (const source of definition.sources) {
    candidates.push(
      ...Object.entries(buildTemplatePreviewColumnLabels(previewRows, source.query)).map(
        ([field, label]) => ({ field, label }),
      ),
    );
  }

  for (const field of definition.fields) {
    const label = formatReadablePreviewLabel(field.alias);
    if (label) {
      candidates.push({ field: field.alias, label });
    }
  }

  for (const field of definition.computedFields ?? []) {
    const label = formatReadablePreviewLabel(field.name);
    if (label) {
      candidates.push({ field: field.name, label });
    }
  }

  return buildPreviewColumnLabelMap(previewRows, candidates);
}

function formatTemplateTimeRangeLabel(
  query: TemplateQuery | undefined,
  defaultTimeRange?: unknown,
  contextualLabels: string[] = [],
): string | null {
  const timeRange = query?.timeRange;
  if (!timeRange) {
    return null;
  }

  if (isConcreteTimeRangePreset(timeRange.preset)) {
    return timeRange.preset.trim();
  }

  if (isNonEmptyString(defaultTimeRange)) {
    return defaultTimeRange.trim();
  }

  const contextualPeriodLabel = getContextualPeriodLabel(contextualLabels);
  if (contextualPeriodLabel && (timeRange.from || timeRange.to)) {
    return contextualPeriodLabel;
  }

  if (timeRange.from) {
    return 'custom range';
  }

  if (timeRange.to) {
    return [timeRange.to ? `to ${timeRange.to}` : null]
      .filter(isNonEmptyString)
      .join(' ') || 'custom range';
  }

  return timeRange.field || null;
}

function getContextualPeriodLabel(labels: string[]): string | null {
  const text = labels.join(' ').toLowerCase();
  if (/\btoday\b/.test(text)) {
    return 'today';
  }
  if (/\byesterday\b/.test(text)) {
    return 'yesterday';
  }

  return null;
}

function isConcreteTimeRangePreset(value: string | undefined): value is string {
  return isNonEmptyString(value) && !value.includes('{{');
}

function formatCompositeTimeRangeLabel(definition: CompositeQueryDefinition): string | null {
  const labels = uniqueStrings(
    definition.sources.flatMap((source) => {
      const timeRangeLabel = formatTemplateTimeRangeLabel(source.query, undefined, [
        source.id,
        source.label ?? '',
        ...source.query.measures.flatMap((measure) => [
          measure.alias ?? '',
          measure.field,
        ]),
      ]);
      return timeRangeLabel ? [`${source.label ?? source.id}: ${timeRangeLabel}`] : [];
    }),
  );

  if (labels.length === 0) {
    return null;
  }

  return labels.join('; ');
}

function formatLimitLabel(limit: number | undefined): string | null {
  return typeof limit === 'number' ? String(limit) : null;
}

function formatJoinSummary(definition: CompositeQueryDefinition): string | null {
  const joinKeyLabels = definition.joinKeys.map(formatJoinKeyLabel).filter(isNonEmptyString);
  const keySummary = joinKeyLabels.length > 0 ? ` on ${joinKeyLabels.join(', ')}` : '';

  return `${definition.joinType} join${keySummary}`;
}

function formatMatchingSummary(definition: CompositeQueryDefinition): string | null {
  const primarySource = definition.sources.find((source) => source.id === definition.primarySourceId);
  const primaryLabel = primarySource?.label ?? primarySource?.id ?? definition.primarySourceId;
  const keyLabels = definition.joinKeys.map(formatJoinKeyLabel).filter(isNonEmptyString);

  if (keyLabels.length === 0) {
    return `Primary source: ${primaryLabel}`;
  }

  return `Primary source: ${primaryLabel}; matches ${keyLabels.join(', ')}`;
}

function formatJoinKeyLabel(joinKey: CompositeJoinKey): string {
  const kindLabel = joinKey.kind ? ` (${joinKey.kind}${joinKey.granularity ? ` ${joinKey.granularity}` : ''})` : '';
  return `${joinKey.left} = ${joinKey.right}${kindLabel}`;
}

function getReviewNote(metadata: CreateResultMetadata): string | null {
  if (metadata.reviewReason) {
    return metadata.reviewReason;
  }

  if (metadata.needsReview) {
    return metadata.saveDisabledReason ?? 'Review this generated template before saving.';
  }

  return null;
}

function formatCountLabel(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
